import {useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import TableFunction from '../../../../components/table/TableFunction';
import { SimpleSearch } from '../../../../reduxToolkit/Search/SearchApi';
import CustomTh from '../../../../components/table/CustomTh';
import CustomTd from '../../../../components/table/CustomTd';
import { DATE_TIME_FORMAT_NAME_MODEL, getDateFormat, getTimeFormat } from '../../../../Apis/functions';
import { SEARCH_MODEL } from '../../../../components/table/Model/TableModel';
import CustomImageTable from '../../../../components/CustomImageTable';
import CustomChip from '../../../../components/CustomChip';
import { IconAvatarTable } from '../../../../components/buttons/IconButtons';
import { colors } from '../../../../enums/colors';
import ServicesAccessMethod from "../SubComponents/ServicesAccessMethod";
import {GetAllCafeterias} from "../../../../reduxToolkit/FoodieShift/FoodieShiftApi";

/**
 * @Author      Susana García Rodríguez
 * @JobTitle    Frontend Developer
 * @Email       sgarcia@ibl.mx
 */

const HistoryApp = ({
                        item,
                        setItem,
                        setBodyForExport,
                        option,
                        modified,
                        setModified,
                        setEmptyFields
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const tableId = 'tbl_ha_history';
    const searchId = 'txt_ha_history';
    const [results, setResults] = useState([]);
    const isFirstRenderFilters = useRef(true);
    const isFirstRenderSearch = useRef(true);
    const isFirstRenderDefault = useRef(true);
    const [cafeteriaOptions, setCafeteriaOptions] = useState([]);
    const [searchText, setSearchText] = useState('');
    const dateTime = item?.date instanceof Date ? item.date.getTime() : null;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999);
    /**
     * Builds the table header with translated column names.
     *
     * @returns {JSX.Element[]} array of table header cells
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} classes='first_head'>
                {t('name')}
            </CustomTh>,
            <CustomTh tableId={tableId}>{t('date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('delivery')}</CustomTh>,
        ];
    };

    /**
     * Builds the table body from the fetched results.
     *
     * <p>
     * If there is no content, returns empty body and data arrays.
     * Otherwise, maps each result into table cells including
     * employee info, formatted date, time, and delivery method.
     * </p>
     *
     * @returns {{body: JSX.Element[], data: Array}} table body and raw data
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) {
            return {
                body: [],
                data: [],
            };
        }

        const body = results?.content?.map((item) => {
            return (
                <>
                    <CustomTd tableId={tableId} classes={'first-column'}>
                        <CustomImageTable image={IconAvatarTable} containerClass={'d-flex align-items-center'}>
                            <div className={'ml-1'}>
                                <p className={'mb-1'}>{item?.fullName}</p>
                                <CustomChip
                                    legend={item?.employeeId}
                                    background={colors.secondary100}
                                    fontColor={colors.secondary700}
                                    borderColor={colors.secondary150}
                                    variant={'compact'}
                                />
                            </div>
                        </CustomImageTable>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {getDateFormat(item?.entryDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {getTimeFormat(item?.entryDate)}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <ServicesAccessMethod row={item} t={t} />
                    </CustomTd>
                </>
            );
        });

        return { body, data: results?.content };
    };

    /**
     * Fetches paginated history data from the backend.
     *
     * <p>
     * If no date is selected, the request is skipped and the empty field
     * state is handled accordingly. Otherwise, builds the search payload,
     * stores it for export, and dispatches the paginated search request.
     * </p>
     *
     * @param {number} page current page number to fetch
     * @returns {void}
     */
    const getData = (page) => {
        if (!item.date) {
            /**
             * If no date is selected, do not execute the API.
             * On subsequent renders, mark the required field as empty.
             */
            if (!isFirstRenderDefault.current) {
                setEmptyFields(true);
            } else {
                isFirstRenderDefault.current = false;
            }

            return;
        }

        setEmptyFields(false);
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: option,
            attendanceStartDate: item.date.getTime(),
            modelId: item.cafeteriaId,
            key: item.search
        });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({
                payload: {
                    data: { data, success },
                },
            }) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    };

    /**
     * Initializes table helpers such as header, body, pagination,
     * rows per page, and data loading behavior.
     */
    const { thead, tbody, pagination, rowsPerPage } = TableFunction({
        data: results,
        getThead,
        getTbody,
        hasContainer: true,
        labelRowsPerPage: 'services_per_page',
        fixedPagination: false,
        getData,
        modified,
        setModified
    });

    /**
     * Handles search input changes.
     *
     * <p>
     * Skips execution on first render to avoid triggering
     * unnecessary search updates.
     * </p>
     *
     * @param {string} value search text entered by the user
     * @returns {void}
     */
    const handleSearch = (value) => {
        if (isFirstRenderSearch.current) {
            isFirstRenderSearch.current = false;
            return;
        }

        setItem((prev) => ({ ...prev, search: value }));
        setModified(true);
    };

    /**
     * Resets the local search text state.
     *
     * @returns {void}
     */
    const onReset = () => {
        setSearchText("");
    };

    /**
     * Marks the table as modified when date or cafeteria filters change.
     *
     * <p>
     * Skips execution on first render to prevent unnecessary reloads.
     * </p>
     */
    useEffect(() => {
        if (isFirstRenderFilters.current) {
            isFirstRenderFilters.current = false;
            return;
        }

        setModified(true);
    }, [dateTime, item.cafeteriaId]);

    /**
     * Loads the cafeteria catalog on component mount.
     *
     * <p>
     * Retrieves all cafeterias and stores them in local state
     * for filter selection usage.
     * </p>
     */
    useEffect(() => {
        dispatch(GetAllCafeterias()).then(({ payload: { data: { data } } }) => {
            setCafeteriaOptions(data);
        });
    }, []);

    return {
        t,
        searchId,
        tableId,
        thead,
        tbody,
        pagination,
        cafeteriaOptions,
        searchText,
        setSearchText,
        handleSearch,
        onReset,
        yesterday
    };
};

export default HistoryApp;
